import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Plus, Calendar, Eye, Trash2, PhoneCall, RefreshCw, Search, Layers, Clock, CalendarClock, Loader2, CheckCircle2, PhoneMissed, Voicemail, XCircle, ArrowLeft } from 'lucide-react';
import patientCallingService, { PatientCallBatch, PatientServiceLog } from '../../../services/patientCalling.service';
import ManualAddPatient from './ManualAddPatient';
import ScheduleCallDrawer from './ScheduleCallDrawer';
import Table from '../../Table/Table';
import CommonPagination from '../../Common/CommonPagination';
import Loading from '../../Common/Loading';
import HeaderCrumbs from '../../Common/HeaderCrumbs';
import { Tooltip } from '../../Common/Tooltip';
import ConfirmModal from '../../Common/ConfirmModal';
import styles from './BatchDetail.module.css';

const STATUS_COLORS: Record<string, string> = {
    PENDING: '#9ca3af', SCHEDULED: '#3b82f6', IN_PROGRESS: '#f59e0b',
    COMPLETED: '#10b981', NO_ANSWER: '#f97316', VOICEMAIL: '#a855f7', FAILED: '#ef4444',
};
const STATUS_BG: Record<string, string> = {
    PENDING: '#f3f4f6', SCHEDULED: '#dbeafe', IN_PROGRESS: '#fef3c7',
    COMPLETED: '#dcfce7', NO_ANSWER: '#ffedd5', VOICEMAIL: '#ede9fe', FAILED: '#fee2e2',
};
const CALL_TYPE_LABELS: Record<string, string> = {
    payment_reminder: 'Payment Reminder', patient_info_gathering: 'Patient Info Gathering',
};

const BatchDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [batch, setBatch] = useState<PatientCallBatch | null>(null);
    const [records, setRecords] = useState<PatientServiceLog[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [loading, setLoading] = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [recordsLoading, setRecordsLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [showAddPatient, setShowAddPatient] = useState(false);
    const [showSchedule, setShowSchedule] = useState(false);
    const [filterStatus, setFilterStatus] = useState('');
    const [search, setSearch] = useState('');
    const [recordToDelete, setRecordToDelete] = useState<PatientServiceLog | null>(null);
    const [deletingRecord, setDeletingRecord] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedRecords, setSelectedRecords] = useState<PatientServiceLog[]>([]);
    const [callType, setCallType] = useState<'payment_reminder' | 'patient_info_gathering'>('payment_reminder');
    useEffect(() => { loadAll(); }, [id]);
    useEffect(() => { loadAllRecords(); }, [currentPage, itemsPerPage, filterStatus, debouncedSearch]);

const loadAll = async () => {
    setLoading(true);

    try {
        const r = await patientCallingService.listServiceLogs({
            page: currentPage + 1,
            page_size: itemsPerPage,
            search: debouncedSearch || undefined,
        });

        setRecords(r.items);
        setTotal(r.total);

    } catch (e) {
        console.error(e);
    } finally {
        setLoading(false);
        setIsInitialLoading(false);
    }
};
    const loadAllRecords = async () => {
        setLoading(true);
        try {
            const res = await patientCallingService.getAllRecords({
                page: currentPage + 1,
                page_size: itemsPerPage,
                search: debouncedSearch || undefined,
            });
            setRecords(res.data ?? []);
            setTotal(res.total ?? 0);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setIsInitialLoading(false);
        }
    };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
        const result = await patientCallingService.importFile(file, callType);

        alert(`Successfully imported ${result.imported} records`);
        loadAll();

    } catch (err: any) {
        alert(err.message || 'Import failed');
    } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }
};

    const handleDeleteRecord = (row: PatientServiceLog, e: React.MouseEvent) => {
        e.stopPropagation();
        setRecordToDelete(row);
    };

    const confirmDeleteRecord = async () => {
        if (!recordToDelete) return;
        setDeletingRecord(true);
        try {
            await patientCallingService.deleteServiceLog(recordToDelete.id);
            setRecords(prev => prev.filter(r => r.id !== recordToDelete.id));
            setRecordToDelete(null);
        } catch {
            alert('Failed to delete record');
        } finally {
            setDeletingRecord(false);
        }
    };

    if (isInitialLoading) return <div className={styles.page}><Loading message="Loading batch..." /></div>;
    if (!batch) return <div className={styles.page}><div className={styles.loading}>Batch not found</div></div>;

    const stats = batch.stats;
    const callTypeLabel = CALL_TYPE_LABELS[batch.call_type] || batch.call_type;

    const recordColumns = [
        {
            key: 'patient_name',
            header: 'Patient Name',
            render: (value: string) => (
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{value}</span>
            ),
        },
        {
            key: 'phone_number',
            header: 'Phone',
            render: (value: string | null) =>
                value || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>,
        },
        {
            key: 'dos',
            header: 'DOS',
            render: (value: string | null) =>
                value || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>,
        },
        batch.call_type === 'payment_reminder'
            ? {
                key: 'total_balance',
                header: 'Total Balance',
                render: (value: number | null) =>
                    value != null
                        ? <span style={{ fontWeight: 600 }}>${value.toFixed(2)}</span>
                        : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>,
            }
            : {
                key: 'reason_for_call',
                header: 'Reason for Call',
                render: (value: string | null) => (
                    <span style={{ display: 'inline-block', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {value || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>}
                    </span>
                ),
            },
        {
            key: 'insurance_name',
            header: 'Insurance',
            render: (value: string | null) =>
                value || <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>,
        },
        {
            key: 'call_status',
            header: 'Call Status',
            render: (value: string | null) => {
                if (!value) return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>;
                const fg = STATUS_COLORS[value] || '#6b7280';
                const bg = STATUS_BG[value] || '#f3f4f6';
                return (
                    <span
                        className={styles.statusPill}
                        style={{
                            background: bg,
                            color: fg,
                            borderColor: fg + '33',
                        }}
                    >
                        <span className={styles.statusPillDot} style={{ background: fg }} />
                        {value.replace('_', ' ')}
                    </span>
                );
            },
        },
        {
            key: 'last_called_at',
            header: 'Last Called',
            render: (value: string | null) =>
                value
                    ? new Date(value).toLocaleDateString()
                    : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>N/A</span>,
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (_: any, row: PatientServiceLog) => (
                <div style={{ display: 'flex', gap: '8px' }} onClick={e => e.stopPropagation()}>
                    <Tooltip content="View Detail">
                        <button
                            className={`${styles.actionBtn} ${styles.view}`}
                            onClick={() => navigate(`/patient-calling/records/${row.id}`, { state: { batchId: id, batchName: batch.name } })}
                        >
                            <Eye size={14} />
                        </button>
                    </Tooltip>
                    <Tooltip content="Delete">
                        <button
                            className={`${styles.actionBtn} ${styles.delete}`}
                            onClick={e => handleDeleteRecord(row, e)}
                        >
                            <Trash2 size={14} />
                        </button>
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <div className={styles.page}>
            <HeaderCrumbs>
                <span>{batch.name}</span>
            </HeaderCrumbs>

            {/* Batch Header */}
            <div className={styles.batchHeader}>
                <button
                    type="button"
                    className={styles.backButton}
                    onClick={() => navigate('/patient-calling')}
                    title="Back to Patient Calling"
                >
                    <ArrowLeft size={16} />
                </button>
                <div className={styles.batchHeaderLeft}>
                    <h1 className={styles.pageTitle}>{batch.name}</h1>
                    <div className={styles.batchMeta}>
                        <span className={`${styles.badge} ${batch.call_type === 'payment_reminder' ? styles.badgeGreen : styles.badgePurple}`}>
                            {callTypeLabel}
                        </span>
                        {batch.client_name && <span className={styles.mutedText}>{batch.client_name}</span>}
                        <span className={styles.mutedText}>Created {batch.created_at ? new Date(batch.created_at).toLocaleDateString() : '—'}</span>
                    </div>
                </div>
                <div className={styles.actionRow}>
                    <button className={styles.ghostIconBtn} onClick={loadAllRecords} title="Refresh">
                        <RefreshCw size={14} />
                    </button>
                    <input ref={fileInputRef} type="file" accept=".xls,.xlsx,.csv" style={{ display: 'none' }} onChange={handleFileChange} />
                    <button className={styles.actionPillBtn} onClick={() => fileInputRef.current?.click()} disabled={importing}>
                        <Upload size={14} /> {importing ? 'Importing...' : 'Import File'}
                    </button>
                    <button className={styles.actionPillBtn} onClick={() => setShowAddPatient(true)}>
                        <Plus size={14} /> Add Patient
                    </button>
                    <button className={`${styles.actionPillBtn} ${styles.actionPillBtnPrimary}`} onClick={() => setShowSchedule(true)}>
                        <Calendar size={14} /> Schedule Calls
                    </button>
                </div>
            </div>

            {/* Stats Strip */}
            {stats && (
                <div className={styles.statsStrip}>
                    {[
                        { label: 'Total', val: stats.total, color: '#64748b', icon: Layers },
                        { label: 'Pending', val: stats.pending, color: '#9ca3af', icon: Clock },
                        { label: 'Scheduled', val: stats.scheduled, color: '#3b82f6', icon: CalendarClock },
                        { label: 'In Progress', val: stats.in_progress, color: '#f59e0b', icon: Loader2 },
                        { label: 'Completed', val: stats.completed, color: '#10b981', icon: CheckCircle2 },
                        { label: 'No Answer', val: stats.no_answer, color: '#f97316', icon: PhoneMissed },
                        { label: 'Voicemail', val: stats.voicemail, color: '#a855f7', icon: Voicemail },
                        { label: 'Failed', val: stats.failed, color: '#ef4444', icon: XCircle },
                    ].map(s => {
                        const statusKey = s.label.toUpperCase().replace(' ', '_');
                        const isActive = filterStatus === statusKey;
                        const isTotal = s.label === 'Total';
                        const Icon = s.icon;
                        return (
                            <div
                                key={s.label}
                                className={`${styles.statChip} ${isActive ? styles.statChipActive : ''}`}
                                style={{ ['--chip-color' as any]: s.color }}
                                onClick={() => {
                                    if (isTotal) {
                                        setFilterStatus('');
                                    } else {
                                        setFilterStatus(prev => (prev === statusKey ? '' : statusKey));
                                    }
                                }}
                            >
                                <div className={styles.statChipIcon}>
                                    <Icon size={16} strokeWidth={2.25} />
                                </div>
                                <div className={styles.statChipBody}>
                                    <span className={styles.statChipValue}>{s.val}</span>
                                    <span className={styles.statChipLabel}>{s.label}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Records Table (client-style) */}
            <div className={styles.tableSection}>
                <div className={styles.tableHeader}>
                    <h2>
                        <PhoneCall size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Patient Records
                    </h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
                        <div style={{ position: 'relative', width: '280px' }}>
                            <Search
                                size={16}
                                style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                }}
                            />
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder="Search by patient name..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        if (currentPage !== 0) setCurrentPage(0);
                                        else loadAllRecords();
                                    }
                                }}
                            />
                        </div>
                        <button
                            className={styles.addBtn}
                            onClick={() => {
                                if (currentPage !== 0) setCurrentPage(0);
                                else loadAllRecords();
                            }}
                        >
                            <Search size={14} /> Search
                        </button>
                    </div>
                </div>

                {records.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
                        <PhoneCall size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                        <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600 }}>
                            No patient records
                        </h3>
                        <p style={{ margin: 0, fontSize: '14px' }}>
                            {search || filterStatus
                                ? 'Try adjusting your search or filter'
                                : 'Import a file or add patients manually'}
                        </p>
                    </div>
                ) : (
                    <Table
                        columns={recordColumns}
                        data={records}
                        maxHeight="calc(100vh - 400px)"
                        onRowClick={(row) => navigate(`/patient-calling/records/${row.id}`, { state: { batchId: id, batchName: batch.name } })}
                    />
                )}

                {(recordsLoading || (loading && !isInitialLoading)) && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255, 255, 255, 0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                        }}
                    >
                        <Loading message="Updating..." />
                    </div>
                )}
            </div>

            <CommonPagination
                show={total > 0}
                pageCount={Math.ceil(total / itemsPerPage)}
                currentPage={currentPage}
                totalItems={total}
                itemsPerPage={itemsPerPage}
                onPageChange={(data) => setCurrentPage(data.selected)}
                onItemsPerPageChange={(items) => {
                    setItemsPerPage(items);
                    setCurrentPage(0);
                }}
            />

            {showAddPatient && (
                <ManualAddPatient
                    callType={batch.call_type}
                    onClose={() => setShowAddPatient(false)}
                    onSaved={() => { setShowAddPatient(false); loadAll(); }}
                />
            )}

            {showSchedule && (
                <ScheduleCallDrawer
                    selectedServiceLogIds={selectedRecords.map(r => r.id)}
                    batchName={batch.name}
                    totalRecords={stats?.total || 0}
                    onClose={() => setShowSchedule(false)}
                    onScheduled={() => { setShowSchedule(false); loadAll(); }}
                />
            )}

            <ConfirmModal
                isOpen={!!recordToDelete}
                onClose={() => !deletingRecord && setRecordToDelete(null)}
                onConfirm={confirmDeleteRecord}
                title="Remove Patient"
                message={`Remove "${recordToDelete?.patient_name ?? 'this patient'}" from the batch? This cannot be undone.`}
                confirmText="Remove"
                type="danger"
                loading={deletingRecord}
            />
        </div>
    );
};

export default BatchDetail;
